import { useState, useEffect } from "react";
import Loader from "components/Loader";
import ArticleHeadlinesKeywordTable from "components/Tables/ArticleHeadlinesKeywordTable";
import MobileSupportTabBar from "components/TabBars/MobileSupportTabBar";
import HelpCard from "components/Cards/HelpCard";
import { Button, Col, Form } from "react-bootstrap";
import Validator from "utils/Validator";
import { get_seo_meta_description_for_single_keyword } from "services/buddyXApiHubService";
import classNames from "classnames";
import { css } from "aphrodite";
import pageStyles from "styles/pageStyles";
import InsufficientCreditsPopup from "components/InsufficientCreditsPopup";
import ToastAlert from "components/Alert/ToastAlert";
import { getInputFieldStyle, getButtonStyle } from "utils/formStyles";

export default function Page() {
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResultGot, setIsResultGot] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [brandName, setBrandName] = useState("");
  const [showSearchItem, setShowSearchItem] = useState("");
  const [insufficientCreditsData, setInsufficientCreditsData] = useState(null);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    brand_name: "",
    keywords: [],
  });
  const [errors, setErrors] = useState({ keyword: "", brandName: "" });
  const [tabs, setTabs] = useState([]);
  const firstLevelTopic = "SEO";
  const secoundLevelTopic = "Meta Description";
  const thirdLevelTopic = "Single Keyword";

  const handleKeywordChange = (e) => {
    setKeyword(e.target.value);
  };

  const handleBrandNameChange = (e) => {
    setBrandName(e.target.value);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = "";
    if (
      name === "keyword" &&
      !Validator.validateRequired(keyword, Validator.validateSingleKeyword)
    ) {
      error = "Please enter keywords";
    } else if (
      name === "brandName" &&
      !Validator.validateRequired(value, Validator.validateBrandName)
    ) {
      error = "Please enter brand name";
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handleReset = () => {
    setShowSearchItem("");
    setIsResultGot(false);
    setKeyword("");
    setBrandName("");
    setShowErrorNotification(false);
    setErrorMessage("");
    setErrors({ keyword: "", brandName: "" });
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const result = await get_seo_meta_description_for_single_keyword(
        formData,
        handleInsufficientCredits
      );
      setShowSearchItem(formData.keywords[0]);
      setTabs(
        Object.keys(result).map((key, index) => ({
          label: `Draft ${String.fromCharCode(65 + index)}`,
          element: (
            <ArticleHeadlinesKeywordTable
              data={result[key]}
              topic={thirdLevelTopic}
            />
          ),
        }))
      );
      setIsResultGot(true);
    } catch (error) {
      console.error("Submission error:", error);
      setErrorMessage(
        error.message ||
          "Failed to generate meta descriptions. Please try again."
      );
      setShowErrorNotification(true);
      setIsResultGot(false);
    }
    setIsLoading(false);
  };

  const handleInsufficientCredits = (balanceData) => {
    setInsufficientCreditsData(balanceData);
    setShowInsufficientCredits(true);
  };

  const closeInsufficientCredits = () => {
    setShowInsufficientCredits(false);
    setInsufficientCreditsData(null);
  };

  useEffect(() => {
    let isValid =
      Validator.validateRequired(brandName, Validator.validateBrandName) &&
      Validator.validateRequired(keyword, Validator.validateSingleKeyword);
    if (isValid) {
      setFormData({
        brand_name: brandName,
        keywords: [keyword],
      });
    }
    setIsFormValid(isValid);
  }, [brandName, keyword]);

  return (
    <div className={classNames("container-fluid", css(pageStyles.container))}>
      {/* Toast Notifications */}
      <ToastAlert
        show={showErrorNotification}
        message={errorMessage}
        type="error"
        icon="fas fa-exclamation-circle"
        duration={5000}
        onClose={() => setShowErrorNotification(false)}
        position="top-right"
      />
      <h1 className="d-flex fw-normal justify-content-start align-items-center fw-bold">
        {secoundLevelTopic}
      </h1>
      <p className="fw-normal justify-content-start align-items-center">
        {firstLevelTopic} / {secoundLevelTopic} / <b>{thirdLevelTopic}</b>
      </p>
      <p className="fw-normal justify-content-start align-items-center">
        Incorporating keywords into the SEO Meta Description enhances the
        snippet's relevance in search engine results pages (SERPs), signaling
        topical alignment to algorithms like Google's while boosting
        click-through rates (CTR) by providing context that matches user
        queries. Although meta descriptions are not a direct ranking factor,
        their keyword integration supports semantic understanding in
        AI-influenced search environments as of 2025, potentially increasing
        visibility in featured snippets or AI Overviews and contributing to
        10-20% higher engagement when displayed. This practice also reinforces
        on-page SEO signals, reduces mismatched traffic, and strengthens overall
        content authority in competitive niches.
      </p>
      <div className="row">
        <div className="col-lg-9 col-md-10 col-sm-10">
          <HelpCard
            topic={"How To Use"}
            description={
              "Integrate the primary keyword naturally near the beginning of the meta description to prioritize relevance, aiming for a length of 140-160 characters to ensure full display in SERPs without truncation. Craft compelling, user-intent-focused copy with a call-to-action (e.g., 'Discover top strategies for [Primary Keyword] and boost your results today | Brand Name'), avoiding keyword stuffing to maintain readability and comply with guidelines. Implement via HTML <meta name='description' content='...''> tags or CMS tools."}
            image={"/icon/pin.svg"}
          />
        </div>
      </div>
      <div className="p-2 mb-2 rounded">
        <div className="d-flex fw-bold mb-0">Generate Meta Description</div>
        <div className="row mb-2 align-items-end">
          <div className="mb-2">
            <p className="fw-normal justify-content-start align-items-center">
              Enter your keyword and brand name to generate optimized meta
              descriptions.
            </p>
            <Form.Group>
              <div className="d-flex mt-2">
                <Col sm={4} className="me-2">
                  <Form.Control
                    type="text"
                    name="keyword"
                    placeholder="Enter keyword"
                    value={keyword}
                    onChange={handleKeywordChange}
                    onBlur={handleBlur}
                    isInvalid={!!errors.keyword}
                    required
                    className="px-2"
                    style={getInputFieldStyle('type3')}
                  />
                </Col>
                <Col sm={4} className="me-2">
                  <Form.Control
                    type="text"
                    name="brandName"
                    placeholder="Enter brand name"
                    value={brandName}
                    onChange={handleBrandNameChange}
                    onBlur={handleBlur}
                    isInvalid={!!errors.brandName}
                    required
                    className="px-2"
                    style={getInputFieldStyle('type3')}
                  />
                </Col>
                <div className="col-lg-4 col-md-4 col-sm-12 mb-2 me-1">
                  <div className="d-flex justify-content-start align-items-end">
                    <Button
                      variant="dark"
                      className="me-1"
                      onClick={handleSubmit}
                      disabled={!isFormValid || isLoading}
                      style={getButtonStyle('type3', 'primary')}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Generating...
                        </>
                      ) : (
                        "Search"
                      )}
                    </Button>
                    <Button
                      variant="outline-dark"
                      className="me-1"
                      onClick={handleReset}
                      style={getButtonStyle('type3', 'outline')}
                    >
                      Reset
                    </Button>
                  </div>
                </div>
              </div>
            </Form.Group>
          </div>
        </div>
      </div>

      <div className="p-2">
        {showSearchItem !== "" && (
          <div>
            <p className="d-flex fw-bold mb-0">
              Meta descriptions for
              <span className="search-item">&nbsp;"{showSearchItem}"</span>
            </p>
          </div>
        )}
        {isLoading && <Loader />}
        {isResultGot && <MobileSupportTabBar tabs={tabs} />}
      </div>
      {/* Insufficient Credits Popup */}
      <InsufficientCreditsPopup
        isOpen={showInsufficientCredits}
        onClose={closeInsufficientCredits}
        currentBalance={insufficientCreditsData?.currentBalance}
        requiredCredits={insufficientCreditsData?.requiredCredits}
        shortfall={insufficientCreditsData?.shortfall}
        actionName={insufficientCreditsData?.actionName}
      />
    </div>
  );
}
